import * as React from "react";
import { useReducer, useEffect } from "react";
import { Row, Col, Button, Input } from 'reactstrap';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import GenericDropdown from './controls/GenericDropdown';
import { LookupItem } from "../Models/LookupItem";
import CheckboxList from "./controls/CheckboxList";
import EventsListingResults, { SearchFilter, EventItem } from './EventsListingResult';
import FeaturedEvent from "./FeaturedEvent";
import * as Utils from '../Utils';

type SortItemValue = {
    field: 'date' | 'alpha';
    sortAsc: boolean;
}

type SortItem = {
    Value: SortItemValue;
    Title: string;
}

const sortItems: SortItem[] = [{
    Title: 'Alpha',
    Value: {
        field: 'alpha',
        sortAsc: false
    }
}, {
    Title: 'Date ASC',
    Value: {
        field: 'date',
        sortAsc: true
    }
}, {
    Title: 'Date DESC',
    Value: {
        field: 'date',
        sortAsc: false
    }
}];

const defaultSortItem = sortItems[1];

type EventsListingState = {
    keywords: string;
    selectedCategory: LookupItem;
    selectedAudience: LookupItem;
    selectedLocations: LookupItem[];
    searchFilter: SearchFilter;
    orderBy: SortItemValue;
    initDone: boolean;
}

const initialState: EventsListingState = {
    keywords: '',
    selectedCategory: undefined,
    selectedAudience: undefined,
    selectedLocations: [],
    searchFilter: undefined,
    orderBy: defaultSortItem.Value,
    initDone: false
}


const EventsListing = (props: {
    Path: string;
    Categories: LookupItem[];
    Locations: LookupItem[];
    Audience: LookupItem[];
    FeaturedEvents: EventItem[];
    Summary: string;
}) => {

    const [state, setState] = useReducer(
        (prevState: EventsListingState, newState: Partial<EventsListingState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const search = () => {

        const sf: SearchFilter = {
            Audience: state.selectedAudience ? state.selectedAudience.Value : undefined,
            Category: state.selectedCategory ? state.selectedCategory.Value : undefined,
            Locations: (state.selectedLocations || []).map(x => x.Value),
            Keywords: state.keywords,
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        setState({ searchFilter: sf });
    };

    const searchByCategory = (categoryId: string) => {

        const category = props.Categories.find(x => x.Value == categoryId);

        const sf: SearchFilter = {
            Audience: '',
            Category: category ? category.Value : undefined,
            Locations: [],
            Keywords: '',
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        const newState: Partial<EventsListingState> = {
            selectedCategory: category,
            selectedAudience: undefined,
            selectedLocations: [],
            searchFilter: sf,
            keywords: ''
        }

        setState(newState);
    }

    const cleanFilters = () => {
        const sf: SearchFilter = {
            Audience: '',
            Category: undefined,
            Locations: [],
            Keywords: '',
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        setState({
            selectedAudience: undefined,
            selectedLocations: [],
            selectedCategory: undefined,
            keywords: '',
            searchFilter: sf
        });
    }


    useEffect(() => {

        const hp: HashParams = {
            cat: state.selectedCategory ? state.selectedCategory.Title : undefined,
            aud: state.selectedAudience ? state.selectedAudience.Title : undefined,
            loc: state.selectedLocations.map(x => x.Title).join('|'),
            q: state.keywords,
            sort: state.orderBy.field,
            asc: state.orderBy.sortAsc
        };

        if (state.initDone) {
            window.location.href = Utils.buildHash(hp);
        }

    }, [state.searchFilter]);

    useEffect(() => {

        const newState: Partial<EventsListingState> = {};

        if (location.hash && location.hash.length > 2) {
            const urlParams = Utils.hashStringParse(location.hash) as HashParams;

            // set category
            if (urlParams.cat) {
                newState.selectedCategory = props.Categories.find(x => x.Title.toLowerCase() == urlParams.cat.toLowerCase());
            }

            // set keyword
            if (urlParams.q && urlParams.q.length > 0) {
                newState.keywords = urlParams.q;
            }

            //set audience
            if (urlParams.aud && urlParams.aud.length > 0) {
                newState.selectedAudience = props.Audience.find(x => x.Title.toLocaleLowerCase() == urlParams.aud.toLocaleLowerCase());
            }

            // chapter
            if (urlParams.loc)
                newState.selectedLocations = urlParams.loc.split('|').map(x => props.Locations.find(y => y.Title.toLowerCase() == x.toLowerCase()));

            // order by
            if (urlParams.sort && (urlParams.sort.toLowerCase() == 'date' || urlParams.sort.toLowerCase() == 'alpha')) {

                urlParams.asc = urlParams.asc || false;
                const sortItem = sortItems.find(x => x.Value.field == urlParams.sort.toLowerCase() && x.Value.sortAsc == urlParams.asc);
                newState.orderBy = sortItem ? sortItem.Value : defaultSortItem.Value;
            }
        }

        if (!newState.orderBy) {
            newState.orderBy = defaultSortItem.Value;
        }

        newState.initDone = true;
        newState.searchFilter = {
            Audience: newState.selectedAudience ? newState.selectedAudience.Value : undefined,
            Category: newState.selectedCategory ? newState.selectedCategory.Value : undefined,
            Locations: (newState.selectedLocations || []).map(x => x.Value),
            Keywords: newState.keywords,
            SortByField: newState.orderBy.field,
            SortASC: newState.orderBy.sortAsc,
            Path: props.Path
        };

        setState(newState);

    }, []);



    // return
    return <Row>
        <Col md={8} lg={9}>
            <div className="description" dangerouslySetInnerHTML={{ __html: props.Summary }}>
            </div>
            <div className="events-listing">
                <div className="inline-filters d-flex flex-md-row flex-column flex-sm-column">
                    <div className="inline-filters-items d-xl-flex w-100">
                        <GenericDropdown
                            dataSource={props.Categories}
                            getId={x => x.Value}
                            renderItem={x => x.Title}
                            renderSelectedItem={x => x.Title}
                            nullSelectionText="All Events"
                            allowNullSelection={true}
                            selectedValue={state.selectedCategory ? state.selectedCategory.Value : undefined}
                            onSelectedItemChanged={x => setState({ selectedCategory: x })}
                        />
                        <GenericDropdown
                            dataSource={props.Audience}
                            getId={x => x.Value}
                            renderItem={x => x.Title}
                            renderSelectedItem={x => x.Title}
                            nullSelectionText="All Audience"
                            allowNullSelection={true}
                            selectedValue={state.selectedAudience ? state.selectedAudience.Value : undefined}
                            onSelectedItemChanged={x => setState({ selectedAudience: x })}
                        />
                        <UncontrolledDropdown>
                            <DropdownToggle caret>
                                <i className="fas fa-map-marker-alt"></i> Locations
                            </DropdownToggle>
                            <DropdownMenu>
                                <CheckboxList
                                    dataSource={props.Locations}
                                    getId={x => x.Value}
                                    renderItem={x => x.Title}
                                    selectedValues={state.selectedLocations.map(x => x.Value) || []}
                                    onSelectedItemChanged={(item, checked) => {

                                        let updatedValues = [];

                                        if (checked) {
                                            updatedValues = state.selectedLocations.concat([item]);
                                        } else {
                                            updatedValues = state.selectedLocations.filter(x => x.Value != item.Value);
                                        }

                                        setState({ selectedLocations: updatedValues });
                                    }}
                                />
                            </DropdownMenu>
                        </UncontrolledDropdown >


                        <div className="search">
                            <div className="input-group w-100">
                                <div className="search-icon">
                                    <i className="fas fa-search"></i>
                                </div>
                                <Input onChange={(e) => { setState({ keywords: e.target.value }) }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            search();
                                        }
                                    }}
                                    value={state.keywords}
                                    className="form-control"
                                    placeholder="Search for keywords" />
                                <Button onClick={search}>Search</Button>
                                <Button onClick={cleanFilters} className="btn-clear">Clear Filters</Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="float-right">
                    <GenericDropdown
                        className="order-by"
                        dataSource={sortItems}
                        getId={x => x.Value}
                        renderItem={x => x.Title}
                        renderSelectedItem={x => `Sort by: ${x.Title}`}
                        nullSelectionText="All Events"
                        selectedValue={state.orderBy}
                        onSelectedItemChanged={x => {

                            const sf = { ...state.searchFilter };
                            sf.SortByField = x.Value.field;
                            sf.SortASC = x.Value.sortAsc;

                            setState({
                                searchFilter: sf,
                                orderBy: x.Value
                            });
                        }}
                    />
                </div>
                <div className="clearfix"></div>
                {(props.FeaturedEvents || []).map((x, i) => <FeaturedEvent key={i} item={x} className={"d-block d-md-none " + (i > 0 ? ' mt-3' : '')}  />)}
                <EventsListingResults searchFilter={state.searchFilter} searchByCategory={searchByCategory} />
            </div>
        </Col>
        <Col md={4} lg={3} className="d-none d-md-block">
            {(props.FeaturedEvents || []).map((x, i) => <FeaturedEvent key={i} item={x} className={i > 0 ? 'mt-3' : null} />)}
        </Col>
    </Row>
}

class HashParams {
    cat: string;
    aud: string;
    loc: string;
    q: string;
    sort: string;
    asc: boolean;
}

export default EventsListing;